import React, { useRef, useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'

import 'swiper/css'
import 'swiper/css/pagination'

import whyMaldives1 from '../../../assets/assets-maldives/why/1.jpg'
import whyMaldives2 from '../../../assets/assets-maldives/why/2.jpg'
import whyMaldives3 from '../../../assets/assets-maldives/why/3.jpg'
import whyMaldives4 from '../../../assets/assets-maldives/why/4.jpg'
import whyMaldives5 from '../../../assets/assets-maldives/why/5.jpg'
import whyMaldives6 from '../../../assets/assets-maldives/why/6.jpg'

type CardData = {
    image: string
    key: string
}

const cards: CardData[] = [
    { image: whyMaldives1, key: 'staffRatio' },
    { image: whyMaldives2, key: 'natural' },
    { image: whyMaldives3, key: 'reef' },
    { image: whyMaldives4, key: 'size' },
    { image: whyMaldives5, key: 'villa' },
    { image: whyMaldives6, key: 'budget' },
]

const CardContent: React.FC<{ card: CardData }> = ({ card }) => {
    const { t, i18n } = useTranslation('maldives')
    const isRu = i18n.language === 'ru';
    const badge = t(`whyChoosing.cards.${card.key}.badge`)
    const title = t(`whyChoosing.cards.${card.key}.title`)
    const description = t(`whyChoosing.cards.${card.key}.description`)
    return (
        <>
            <img src={card.image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

            <div className="relative h-full flex flex-col justify-between">
                <span className="leading-none w-fit inline-flex items-center h-5 sm:h-8 lg:h-[42px] px-2.5 text-white text-[10px] sm:text-xs lg:text-lg rounded-[4.4px] sm:rounded-lg lg:rounded-[10px] bg-[#4A90A4]">
                    {badge}
                </span>

                <div className="sm:min-h-[94px]">
                    <h3 className="lg:text-[22px] sm:text-xl text-sm font-medium text-white leading-tight">
                        {title}
                    </h3>

                    <p className={`${isRu ? 'lg:text-base' : 'lg:text-lg'} sm:text-sm text-xs text-white sm:mt-2.5 mt-1`}>
                        {description}
                    </p>
                </div>
            </div>
        </>
    )
}

const WhyChoosingGrid: React.FC = () => {
    const swiperInstanceRef = useRef<SwiperType | null>(null)
    const sliderContainerRef = useRef<HTMLDivElement | null>(null)
    const [isMobile, setIsMobile] = useState<boolean>(false)
    const { i18n } = useTranslation('maldives')
    const isRu = i18n.language === 'ru';

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 1099px)')

        const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
            setIsMobile(e.matches)
        }

        handleMediaChange(mediaQuery)
        mediaQuery.addEventListener('change', handleMediaChange)

        return () => {
            mediaQuery.removeEventListener('change', handleMediaChange)
        }
    }, [])

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!swiperInstanceRef.current) return
            const swiper = swiperInstanceRef.current
            const clickedSlide = (e.target as HTMLElement).closest('.swiper-slide')

            if (!clickedSlide) return

            if (clickedSlide.classList.contains('swiper-slide-next')) {
                swiper.slideNext()
            } else if (clickedSlide.classList.contains('swiper-slide-prev')) {
                swiper.slidePrev()
            }
        }

        const sliderElement = sliderContainerRef.current
        if (sliderElement) {
            sliderElement.addEventListener('click', handleClick)
        }

        return () => {
            if (sliderElement) {
                sliderElement.removeEventListener('click', handleClick)
            }
        }
    }, [isMobile])

    return (
        <section className="lg:py-[60px] sm:py-10 py-8 bg-white">
            <div className="max-w-[1600px] lg:px-10 mx-auto">
                <div className="text-center  mb-6 sm:mb-8 lg:mb-10 px-5 sm:px-6 max-w-[1000px] mx-auto">
                    <h2 className="section-heading">
                        <Trans
                            i18nKey="whyChoosing.heading"
                            ns="maldives"
                            components={{
                                br1: <br className='sm:hidden' />,
                                br2: <br className='hidden sm:block lg:hidden' />,
                                span1: <span className='lg:hidden' />,
                            }}
                        />
                    </h2>

                    <p className="text-xs sm:text-lg text-[#4A4A4A] font-normal mt-2 sm:mt-4 leading-[20px] sm:leading-[29px]">
                        <Trans
                            i18nKey="whyChoosing.subheading"
                            ns="maldives"
                            components={{
                                strong1: <strong className={`${isRu ? '!font-normal sm:!font-bold' : '!font-bold'}`} />,
                                br1: <br className='md:block hidden' />,
                                br2: <br className='hidden md:block' />,
                            }}
                        />
                    </p>
                </div>

                {isMobile ? (
                    <div ref={sliderContainerRef}>
                        <Swiper
                            onSwiper={(swiper: SwiperType) => { swiperInstanceRef.current = swiper }}
                            modules={[Pagination]}
                            slidesPerView="auto"
                            spaceBetween={window.innerWidth > 640 ? 20 : 7.71}
                            slidesOffsetBefore={20}
                            slidesOffsetAfter={20}
                            initialSlide={0}
                            breakpoints={{
                                1099: { slidesOffsetBefore: 40, slidesOffsetAfter: 40 }
                            }}
                            pagination={{
                                clickable: true,
                                el: '.why-choosing-pagination'
                            }}
                        >
                            {Array.from({ length: Math.ceil(cards.length / 2) }, (_, slideIndex) => {
                                const columnsCount = Math.ceil(cards.length / 2)
                                const topCard = cards[slideIndex]
                                const bottomCard = cards[slideIndex + columnsCount]
                                return (
                                    <SwiperSlide key={slideIndex} className={`sm:!w-[70vw] sm:max-w-[456px] ${isRu ? 'max-w-[300px]' : 'max-w-[280px]'} w-full`}>
                                        <div className="flex flex-col gap-[7.71px] sm:gap-5 w-full">
                                            <div className={`${isRu ? 'h-[200px]' : 'h-[160px]'} sm:h-[256px] rounded-lg sm:rounded-[20px] py-4 px-3 sm:!p-5 relative overflow-hidden`}>
                                                <CardContent card={topCard} />
                                            </div>

                                            {bottomCard && (
                                                <div className={`${isRu ? 'h-[200px]' : 'h-[160px]'} sm:h-[256px] rounded-lg sm:rounded-[20px] py-4 px-3 sm:!p-5 relative overflow-hidden`}>
                                                    <CardContent card={bottomCard} />
                                                </div>
                                            )}
                                        </div>
                                    </SwiperSlide>
                                )
                            })}
                        </Swiper>

                        <div className="pagination why-choosing-pagination mt-8" />
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-5">
                        {cards.map((card, index) => (
                            <div
                                key={index}
                                className="h-[320px] rounded-[20px] sm:p-[23px] p-4 relative overflow-hidden"
                            >
                                <CardContent card={card} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}

export default WhyChoosingGrid
